/**
 * Equation numbering, cross-references, and click-to-copy.
 *
 * Textbooks number display equations and refer back to them — "substituting (3)
 * into (7)" — and without that a ten-step derivation forces the reader to
 * scroll hunting for "the equation above". This numbers every display equation
 * in an answer, turns a written `(3)` into a link to it, and makes clicking any
 * equation copy its LaTeX.
 *
 * Done against the rendered DOM rather than as a rehype plugin because the
 * numbers are per *answer*, and KaTeX has already produced the markup by the
 * time we can count them.
 */

import { useEffect } from "react";
import { copyText, equationSource } from "./copy.ts";

const NUMBER_CLASS = "eq-number";
const FLASH_CLASS = "eq-flash";

/**
 * Numbers the equations in `root` and wires the references pointing at them.
 *
 * Re-runs whenever the content changes. Everything it adds is marked, so a
 * re-run cleans up after the previous one rather than double-numbering a
 * streamed answer.
 */
export function useEquations(
  root: React.RefObject<HTMLElement | null>,
  content: string,
  enabled: boolean,
): void {
  useEffect(() => {
    const host = root.current;
    if (!host || !enabled) return;

    const equations = numberEquations(host);
    if (equations.size === 0) return;

    const cleanups = [
      linkReferences(host, equations),
      enableCopyOnClick(host),
    ];

    return () => {
      for (const cleanup of cleanups) cleanup();
    };
  }, [root, content, enabled]);
}

/** Tag each display equation with a number and render it in the margin. */
function numberEquations(host: HTMLElement): Map<number, HTMLElement> {
  const found = new Map<number, HTMLElement>();

  // Clear any numbering from a previous pass over the same node.
  for (const stale of host.querySelectorAll(`.${NUMBER_CLASS}`)) stale.remove();

  const displays = host.querySelectorAll<HTMLElement>(".katex-display");
  let number = 0;

  for (const display of displays) {
    number += 1;
    display.dataset.equation = String(number);
    display.classList.add("eq-numbered");

    const tag = document.createElement("span");
    tag.className = NUMBER_CLASS;
    tag.setAttribute("aria-hidden", "true");
    tag.textContent = `(${number})`;
    display.append(tag);

    found.set(number, display);
  }

  return found;
}

/**
 * Turn a written `(3)` in the prose into a link to equation 3.
 *
 * Only inside paragraphs and list items, and only where a matching equation
 * exists — otherwise "(3)" in "the first three terms (3 of them)" becomes a
 * broken link.
 */
function linkReferences(
  host: HTMLElement,
  equations: Map<number, HTMLElement>,
): () => void {
  const listeners: (() => void)[] = [];
  const pattern = /\((\d{1,2})\)/g;

  const walker = document.createTreeWalker(host, NodeFilter.SHOW_TEXT, {
    acceptNode(node) {
      const parent = node.parentElement;
      if (!parent) return NodeFilter.FILTER_REJECT;
      // Never rewrite inside maths, code, or an existing link.
      if (parent.closest(".katex, code, pre, a, .eq-number")) {
        return NodeFilter.FILTER_REJECT;
      }
      return pattern.test(node.nodeValue ?? "")
        ? NodeFilter.FILTER_ACCEPT
        : NodeFilter.FILTER_REJECT;
    },
  });

  const targets: Text[] = [];
  let current = walker.nextNode();
  while (current) {
    targets.push(current as Text);
    current = walker.nextNode();
  }

  for (const node of targets) {
    const fragment = document.createDocumentFragment();
    const text = node.nodeValue ?? "";
    let index = 0;

    pattern.lastIndex = 0;
    let match = pattern.exec(text);

    while (match) {
      const equation = equations.get(Number(match[1]));
      if (equation) {
        fragment.append(text.slice(index, match.index));

        const link = document.createElement("button");
        link.type = "button";
        link.className = "eq-ref";
        link.textContent = match[0];
        link.setAttribute("aria-label", `Go to equation ${match[1]}`);

        const onClick = () => {
          equation.scrollIntoView({ behavior: "smooth", block: "center" });
          // A flash, because scrolling alone leaves you hunting for which one.
          equation.classList.add(FLASH_CLASS);
          setTimeout(() => equation.classList.remove(FLASH_CLASS), 1200);
        };

        link.addEventListener("click", onClick);
        listeners.push(() => link.removeEventListener("click", onClick));

        fragment.append(link);
        index = match.index + match[0].length;
      }
      match = pattern.exec(text);
    }

    if (index > 0) {
      fragment.append(text.slice(index));
      node.replaceWith(fragment);
    }
  }

  return () => {
    for (const remove of listeners) remove();
  };
}

/** Click any equation to put its LaTeX on the clipboard. */
function enableCopyOnClick(host: HTMLElement): () => void {
  const onClick = (event: MouseEvent) => {
    const target = event.target as HTMLElement | null;
    const katex = target?.closest<HTMLElement>(".katex");
    if (!katex) return;

    // A reference link inside an equation would otherwise be swallowed.
    if (target?.closest(".eq-ref")) return;

    const source = equationSource(katex.closest(".katex-display") ?? katex);
    if (!source) return;

    void copyText(source).then((copied) => {
      if (!copied) return;
      const flashOn = katex.closest<HTMLElement>(".katex-display") ?? katex;
      flashOn.classList.add("eq-copied");
      setTimeout(() => flashOn.classList.remove("eq-copied"), 1200);
    });
  };

  host.addEventListener("click", onClick);
  return () => host.removeEventListener("click", onClick);
}
